import React from "react";
import {Alert, Box} from "@mui/material";
import {DocHeaderTitle, MoreQuestionsFooterText} from "../shopify/HowToIntegrateSkroWithShopifyStore";
import {DocDivider, DocH2, DocImage, DocRoot} from "../Docs";

function OutbrainDSPIntegration() {
    return (
        <DocRoot>
            <Box style={{textAlign: "center", paddingBottom: "12px"}}>
                <a href="https://dsp.outbrain.com" target="_blank" rel="noreferrer">
                    <DocImage sx={{maxWidth: 200}} src="/images/integration/ob-dsp.svg" alt="Outbrain DSP Logo"/>
                </a>
            </Box>
            <DocHeaderTitle title="Outbrain DSP and Skro"/>
            <Box>
                <p>
                    From August 6, 2024, Zemanta will officially rebrand as Outbrain DSP.
                </p>
                <p>
                    With the transition to Outbrain DSP, all Outbrain platforms are unified into one interface,
                    simplifying the buying process and enhancing the overall user experience for advertisers. Don’t
                    worry, Outbrain DSP offers the same functionality as Zemanta, now under a new name to align
                    with the Outbrain family. As an Outbrain DSP user, you can continue to manage your campaigns and
                    activity in exactly the same way that you already know and are familiar with, just under a fresh new
                    brand.
                </p>
                <p>
                    Outbrain DSP integration gives you the ability to manage and optimize all your Outbrain DSP ad
                    campaigns through the Skro interface.
                </p>
                <ul>
                    <li>Sync cost on publisher level every 30 min.</li>
                    <li>Check delivery status of campaigns, ad groups and ads.</li>
                    <li>Pause or resume ad groups, ads and publishers.</li>
                    <li>Change daily budget and bid for ad groups.</li>
                    <li>Check the status of your campaigns, ad groups, ads and placements.
                    </li>
                </ul>
                <p>
                    In this guide, you will learn how to set up your campaigns in Outbrain DSP and Skro so Outbrain DSP
                    can pass information to Skro and back with a selected tracking method. This guide does not cover all
                    aspects and options available when creating a campaign on these two platforms. Basic knowledge
                    regarding setting up campaigns in Outbrain DSP and Skro is required.
                </p>

                <DocH2>Manage Outbrain DSP campaigns through the Skro UI</DocH2>
                <DocDivider/>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-6.png" alt="Manager Outbrain campaigns"
                />

                <DocH2>Manage Outbrain DSP ad groups through the Skro UI</DocH2>
                <DocDivider/>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-7.png" alt="Manager Outbrain ad groups"
                />

                <DocH2>Manage Outbrain DSP content ads through the Skro UI</DocH2>
                <DocDivider/>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-8.png" alt="Manager Outbrain content ads"
                />

                <DocH2>Manage Outbrain DSP publishers through the Skro UI</DocH2>
                <DocDivider/>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-9.png" alt="Manager Outbrain publishers"
                />

                <DocH2>Step 1. Get Outbrain DSP API Credentials</DocH2>
                <DocDivider/>

                <p>
                    Outbrain DSP <strong>API keys</strong>. You can get one by following the steps below:
                </p>
                <p>
                    1. Go to this link and generate your API
                    key: <strong>https://dsp.outbrain.com/o/applications/</strong>
                </p>
                <p>2. Click on <strong>New Application</strong> button.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-10.png" alt="Get Outbrain API token step 1"/>

                <p>3. Enter your API key name where you going to use it.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-11.png" alt="Get Outbrain API token step 2"/>

                <p>4. Copy your <strong>Client ID</strong> and <strong>Client Secret</strong> to your notes.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-12.png" alt="Get Outbrain API token step 3"/>

                <p>
                    5. Once you have everything ready, read the further part of this guide.
                </p>

                <DocH2>Step 2. Add integration</DocH2>
                <DocDivider/>

                <ul>
                    <li>Log in to your <strong>Skro</strong> account.</li>
                    <li>Navigate to <strong>Settings -> Integrations</strong>.</li>
                    <li>In the <strong>Traffic Sources</strong> section click on the <strong>Add</strong> button.</li>
                </ul>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-1.png" alt="Outbrain DSP Integration step 1"
                />
                <ul>
                    <li>Click on <strong>Outbrain DSP</strong> logo and integration form will pop up.</li>
                </ul>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-2.png" alt="Outbrain Integration step 2"
                />
                <ul>
                    <li>
                        Enter your <strong>Integration Name</strong>.
                    </li>
                    <li>
                        Enter your Outbrain DSP <strong>Client ID</strong> and <strong>Client Secret</strong>.
                    </li>
                    <li>Click <strong>Next</strong> button.</li>
                </ul>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-3.png" alt="Outbrain DSP Integration step 3"
                />
                <ul>
                    <li>
                        Integrate your Outbrain DSP Ad Account.
                    </li>
                    <li>Click <strong>Next</strong> button.</li>
                </ul>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-4.png" alt="Outbrain DSP Integration step 4"
                />
                <ul>
                    <li>
                        If you want to send conversions info back to Outbrain DSP, then you will need to
                        enable <strong>Conversion API.</strong>
                    </li>
                    <li>
                        <strong>Conversion Event Name</strong> - must be the same as you typed in Outbrain DSP. When
                        creating Skro campaign you will need to specify which conversion goal to send to.
                    </li>
                    <li>
                        <strong>Include Conversion Payout</strong> - If checked, we will send conversion revenue to
                        Outbrain DSP.
                    </li>
                    <li>
                        Click on <strong>Click & Save</strong> button.
                    </li>
                </ul>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-5.png" alt="Outbrain DSP Integration step 5"
                />
                <ul>
                    <li>Your Outbrain DSP account is successfully integrated.</li>
                    <li>You can switch on/off cost sync with given switch button.</li>
                </ul>
                <DocImage src="/images/docs/ob-integration-6.png" alt="Outbrain Integration step 6"
                />

                <DocH2>Step 3. Associate integration with traffic source</DocH2>
                <DocDivider/>

                <p>
                    1. Go to <strong>Traffic Sources</strong> and click on <strong>Create</strong> button.
                </p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-13.png" alt="Setup Outbrain traffic source Step 1"/>

                <p>2. Click on <strong>Outbrain DSP</strong> logo.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-14.png" alt="Setup Outbrain traffic source step 2"/>

                <p>3. Edit <strong>Postback URL</strong> and add your <strong>Ad Account ID</strong>.</p>
                <p>4. Select your Outbrain DSP integration from the dropdown menu.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-15.png" alt="Setup Outbrain traffic source step 3"/>

                <p>5. All parameters are already pre-filled for you.</p>
                <p>6. Click on <strong>Save & Close</strong> button.</p>
                <p>7. Integration setup is done and your are ready to create your first campaign for Outbrain DSP.</p>

                <DocH2>Step 4. Create a Campaign in Skro</DocH2>
                <DocDivider/>

                <p>
                    1. If your set up needs pre-lander then go to <strong>Landings</strong> view and click
                    on <strong>Create</strong> button to add your landing page.
                </p>
                <p>
                    2. To add your offer link go to <strong>Offers</strong> view and click
                    on <strong>Create</strong> button and add your
                    offer.
                </p>
                <p>4. Select workspace.</p>
                <p>5. Add name.</p>
                <p>6. Add your offer URL and add macros.</p>
                <p>7. Click on <strong>Save & Close</strong> button.</p>
                <DocImage src="/images/docs/google-offer-1.png" alt="Setup Skro Offer"/>

                <p>
                    8. Navigate to the <strong>Campaigns</strong> view and click on <strong>Create</strong> button.
                </p>
                <p>9. Select your integrated <strong>Traffic Source</strong> as Outbrain DSP.</p>
                <p>
                    10. Select your <strong>Conversion Event Name</strong>. That is needed to send conversion info back
                    to Outbrain DSP.
                </p>
                <p>11. Cost Model should be <strong>Auto</strong>.</p>
                <p>12. Select your cost currency.</p>
                <p>13. Click <strong>Next</strong></p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-16.png" alt="Setup Skro Campaign step 1"/>

                <p>14. Select workspace.</p>
                <p>15. Add descriptive name to your campaign.</p>
                <p>16. Click <strong>Next</strong></p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-17.png" alt="Setup Skro Campaign step 2"/>

                <p>17. Choose your <strong>Destination Type</strong>. In this example we use <strong>flow</strong>.</p>
                <p>18. Choose your redirect mode. In this example we use <strong>redirect 302</strong>.</p>
                <p>19. Select your landings. In this example we <strong>use only offers</strong>.</p>
                <p>20. Select your offers.</p>
                <p>21. Click on <strong>Save</strong> button.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-18.png" alt="Setup Skro Campaign step 3"/>

                <p>22. Choose <strong>Redirect</strong> tracking method.</p>
                <p>23. Copy your <strong>Campaign Tracking URL</strong> to clipboard.</p>

                <Alert severity="info" variant="filled" color="info" style={{marginBottom: 8}}>
                    In given example we have that kind of URL:<br/><br/>
                    {"https://skrotrack.com/campaign/dcfe16a1-5b8e-4d30-9e4a-60ea2231437c?postbackid={postbackid}&contentadid={contentadid}&adgroupid={adgroupid}&campaignid={campaignid}&placement={placement}&publisher={publisher}&mediasource={mediasource}&title={title}"}
                </Alert>

                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-19.png" alt="Setup Skro Campaign step 4"/>

                <DocH2>Step 5. Create a Campaign in Outbrain DSP</DocH2>
                <DocDivider/>
                <p>
                    Now you have your Campaign Tracking URL with tracking parameters added. In the next step you will
                    submit it as your item's URL in Outbrain DSP campaign setup. In order to do that, perform the
                    following steps:
                </p>

                <p>1. Sign in to your Outbrain DSP account.</p>
                <p>2. Go to the <strong>Dashboard</strong> view.</p>
                <p>3. Click the <strong>+ Campaign</strong> button and setup your campaign.</p>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-20.png" alt="Setup Outbrain DSP Campaign step 1"/>

                <p>5. Set up your <strong>Ad Group</strong>.</p>
                <p>6. Go to <strong>Inventory</strong> tab and add your tracking parameters from your tracking link.
                    These parameters will be automatically appended to all click-through URLs within this
                    ad group.</p>

                <Alert severity="info" variant="filled" color="success" style={{marginBottom: 8}}>
                    {"postbackid={postbackid}&contentadid={contentadid}&adgroupid={adgroupid}&campaignid={campaignid}&placement={placement}&publisher={publisher}&mediasource={mediasource}&title={title}"}
                </Alert>
                <DocImage src="/images/docs/outbrain-dsp/ob-dsp-21.png" alt="Setup Outbrain DSP Campaign step 2"/>

                <p>
                    7. Finish your ad group setup.
                </p>
                <p>8. Set up your content ad and put on <strong>URL field</strong> your tracking link:</p>

                <Alert severity="info" variant="filled" color="success" style={{marginBottom: 8}}>
                    {"https://skrotrack.com/campaign/dcfe16a1-5b8e-4d30-9e4a-60ea2231437c"}
                </Alert>

                <p>
                    Congratulations, you have successfully set up tracking of Outbrain DSP traffic with Skro.
                    Each time a visitor clicks on your ad, will be directed to your offer page with Skro's tracking
                    parameters being passed.
                </p>
            </Box>
            <MoreQuestionsFooterText/>
        </DocRoot>
    );
}

export default OutbrainDSPIntegration;
